/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import * as React from 'react';
import printer from '../../assets/icons/printer.svg';
import rfid from '../../assets/icons/rfid.svg';
import waiver from '../../assets/icons/waiver.svg';
import { EntitlementType } from '../../entitlementTypes/entitlementType';
import StatusButton from '../../forms/elements/StatusButton';
import { isNilOrEmpty } from '../../language/module';
import { GREEN_CREEK, RED_CREEK } from '../../styles/colors';
import { hasAssociatedRfid } from '../entitlements/media/assocRfid/module';
import { getActiveEntitlementTypesEffectiveToday } from '../entitlements/module';
import { getRequiredWaivers, hasValidWaiver } from '../entitlements/waivers/module';
import { assocRfidDisabledMessage, passPrintDisabledMessage, signWaiverDisabledMessage } from '../featureDisabling';
import { GuestDetails } from '../guestDetails';
import { CIRCULAR_RADIUS } from './guestCardStyles';
import ImmutableMap from '../../language/ImmutableMap';
import { getResource } from '../../language/resources/getResource';
import { IAppState } from '../../core/redux/IAppState';
import { useSelector } from 'react-redux';


const styles = {
  root:   css`
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  `,
  button: css`
    margin: 0 10px;
  `,
};


type Props = {
  guest: GuestDetails;
  hasHadPrintPdf: boolean;
  loadingRfid: boolean;
  loadingWaiver: boolean;
  passEntitlementId: number;
  allEntitlementTypes: ImmutableMap<EntitlementType>;
  onPrintPassClick: () => void;
  onAssocRfidClick: () => void;
  onWaiverButtonClick: () => void;
  rootCss?: SerializedStyles;
}

/**
 * PrimaryActions: a component that renders the Guest Card's primary actions (print, rfid, waiver) that are necessary to
 * get a pass printed and a guest out the door (minus profile image because that's special). Uses its various inputs to
 * handle enable/disable, red/green border, etc.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
const PrimaryActions: React.FC<Props> = props => {
  const resources = useSelector((state: IAppState) => state.locale);
  const disableRfid = React.useMemo(
    () => assocRfidDisabledMessage(resources, props.passEntitlementId, props.guest),
    [resources, props.passEntitlementId, props.guest],
  );

  const disableWaiver = React.useMemo(
    () => signWaiverDisabledMessage(resources, props.passEntitlementId, props.guest),
    [resources, props.passEntitlementId, props.guest],
  );

  const disablePrint = React.useMemo(
    () => passPrintDisabledMessage(resources, props.passEntitlementId, props.guest, props.allEntitlementTypes),
    [resources, props.passEntitlementId, props.guest, props.allEntitlementTypes],
  );

  const allEffectiveEntitlementsHaveValidWaiver = () => {
    let missingWaiver = false;
    const activeEntitlements = getActiveEntitlementTypesEffectiveToday(props.guest, props.allEntitlementTypes);

    // Is there an active entitlement without at least 1 unexpired signed waiver
    activeEntitlements.forEach((entitlement: EntitlementType) => {
      const requiredWaiverVersions = getRequiredWaivers([entitlement]);
      if (requiredWaiverVersions && requiredWaiverVersions?.length > 0) {
        missingWaiver ||= !hasValidWaiver(props.guest, requiredWaiverVersions);
      }
    });
    return !missingWaiver;
  };
  

  return (
    <div css={css`${styles.root} ${props.rootCss}`}>
      <StatusButton
        handleClick={props.onPrintPassClick}
        image={printer}
        borderRadius={CIRCULAR_RADIUS}
        borderColor={props.hasHadPrintPdf || hasAssociatedRfid(props.guest) ? GREEN_CREEK : RED_CREEK}
        isLoading={false} // Never loading.
        toolTipText={getResource(resources, 'PRIMARY_ACTIONS_PRINT_CARD')}
        borderWidth={2}
        height={60}
        width={60}
        disable={!isNilOrEmpty(disablePrint)}
        disableToolTip={disablePrint}
        rootCss={styles.button}
      />
      <StatusButton
        handleClick={props.onAssocRfidClick}
        image={rfid}
        borderRadius={CIRCULAR_RADIUS}
        borderColor={hasAssociatedRfid(props.guest) ? GREEN_CREEK : RED_CREEK}
        isLoading={props.loadingRfid}
        toolTipText={getResource(resources, 'PRIMARY_ACTIONS_LINK_CARD')}
        borderWidth={2}
        height={60}
        width={60}
        disable={!isNilOrEmpty(disableRfid)}
        disableToolTip={disableRfid}
        rootCss={styles.button}
      />
      <StatusButton
        handleClick={props.onWaiverButtonClick}
        image={waiver}
        borderRadius={CIRCULAR_RADIUS}
        borderColor={allEffectiveEntitlementsHaveValidWaiver() ? GREEN_CREEK : RED_CREEK }
        isLoading={props.loadingWaiver}
        toolTipText={allEffectiveEntitlementsHaveValidWaiver() ? getResource(resources, 'PRIMARY_ACTIONS_WAIVER_SIGNED') : getResource(resources, 'PRIMARY_ACTIONS_WAIVER_SIGNATURE')}
        borderWidth={2}
        height={60}
        width={60}
        disable={!isNilOrEmpty(disableWaiver)}
        disableToolTip={disableWaiver}
        rootCss={styles.button}
      />
    </div>
  );
};

export default PrimaryActions;
